import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import parse from "date-fns/parse";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { SERVER_DATE_FORMAT } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { WORKFLOW_REPORT_REFERENCE_DATA_TYPES, WorkflowReportReferenceData } from "@constants/reference-data";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { WorkflowReportMetadataResponse } from "@idw-types/data/reports";
import { WorkflowReportSearchFiltersState } from "@idw-types/state/workflow-report-state";
import {
    setWorkflowReportMetadataSearchFilters,
    setWorkflowReportMetadataSearchResults,
    setWorkflowReportSearchedMetadataSearchResults,
    setWorkflowReportSearchFilters
} from "@redux/actions/workflow-report-actions";
import { initialWorkflowReportState } from "@redux/reducers/workflow-report-reducer";
import {
    getWorkflowReportSearchResultsReduxRequest,
    getWorkflowReportMetadataSearchResultsReduxRequest
} from "@redux-requests/workflow-report";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { CardContainer } from "@containers/common/card-container/card-container";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { WorkflowReportSearchForm } from "./workflow-report-search-form/workflow-report-search-form";
import { WorkflowReportPassRatesContainer } from "./workflow-report-pass-rates-container/workflow-report-pass-rates-container";
import { WorkflowReportCompletionRatesContainer } from "./workflow-report-completion-rates-container/workflow-report-completion-rates-container";
import { WorkflowReportFraudContainer } from "./workflow-report-fraud-container/workflow-report-fraud-container";
import { WorkflowReportStepByStepContainer } from "./workflow-report-step-by-step-container/workflow-report-step-by-step-container";
import { WorkflowReportVolumesByAppIdContainer } from "./workflow-report-volumes-by-app-id-container/workflow-report-volumes-by-app-id-container";
import { WorkflowReportVolumesByCountryContainer } from "./workflow-report-volumes-by-country-container/workflow-report-volumes-by-country-container";
import { CommonReferenceDataPageTypes } from "../../../enums/common-reference-data-page-types";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { getSessionState, getWorkflowReportState } from "@redux/selectors/root-selectors";

export const WorkflowReportContainer = () => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    const [isStartedSearchResults, setIsStartedSearchResults] = useState(false);
    const { session } = useSelector(getSessionState);
    const {
        searchFilters,
        searchResults,
        metadataSearchFilters,
        metadataSearchResults,
        searchedMetadataSearchResults
    } = useSelector(getWorkflowReportState);
    const referenceData = useReferenceDataSelector<WorkflowReportReferenceData>(WORKFLOW_REPORT_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.workflowReport && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                WORKFLOW_REPORT_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.WORKFLOW_REPORT
            );
        }
    }, [referenceData.isLoading.workflowReport, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnAssertions &&
            referenceData.axnCountries &&
            referenceData.axnRelyingParties &&
            referenceData.riskLevels &&
            referenceData.simpleAXNWorkflows &&
            referenceData.documentationLinks
        );
    }, [
        referenceData.axnAssertions,
        referenceData.axnCountries,
        referenceData.axnRelyingParties,
        referenceData.riskLevels,
        referenceData.simpleAXNWorkflows,
        referenceData.documentationLinks
    ]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading.workflowReport;
    }, [referenceData.isLoading.workflowReport]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData;
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    const allowedRelyingParties = useMemo(() => {
        if (referenceData.axnRelyingParties) {
            return extractRelyingPartiesForPropertyName(
                UserPermissionTypes.VIEW_WORKFLOW_USAGE_DETAILS,
                session.user.axnAdminUserRPProperties,
                referenceData.axnRelyingParties
            );
        }

        return [];
    }, [
        referenceData.axnRelyingParties,
        session.user.axnAdminUserRPProperties
    ]);

    const allowedSimpleAxnWorkflows = useMemo(() => {
        if (referenceData.simpleAXNWorkflows) {
            const rpIds = allowedRelyingParties.map(rp => rp.id);
            return referenceData.simpleAXNWorkflows.filter(saw => rpIds.includes(saw.relyingPartyID));
        }

        return [];
    }, [
        allowedRelyingParties,
        referenceData.simpleAXNWorkflows
    ]);

    const handleSearchParams = async () => {
        if (Array.from(searchParams.entries()).length > 0 && isLoaded) {
            const shouldSearchOnLoad = isStringTrue(searchParams.get("shouldSearchOnLoad"));

            const newSearchFilters: WorkflowReportSearchFiltersState = {
                workflowId: searchParams.get("workflowId") || initialWorkflowReportState.searchFilters.workflowId,
                appId: searchParams.get("appId") || initialWorkflowReportState.searchFilters.appId,
                country: searchParams.get("country") || initialWorkflowReportState.searchFilters.country,
                timeRangeType: (searchParams.get("timeRangeType") || initialWorkflowReportState.searchFilters.timeRangeType) as TimeRangeTypes,
                startDate: searchParams.has("startDate")
                    ? parse(searchParams.get("startDate"), SERVER_DATE_FORMAT, new Date())
                    : initialWorkflowReportState.searchFilters.startDate,
                endDate: searchParams.has("endDate")
                    ? parse(searchParams.get("endDate"), SERVER_DATE_FORMAT, new Date())
                    : initialWorkflowReportState.searchFilters.endDate,
                includeArchivedWorkflows: searchParams.has("includeArchivedWorkflows")
                    ? isStringTrue(searchParams.get("includeArchivedWorkflows"))
                    : initialWorkflowReportState.searchFilters.includeArchivedWorkflows
            };

            if (shouldSearchOnLoad) {
                const metadataResponse = await handleMetadataSearch(
                    newSearchFilters.workflowId,
                    newSearchFilters.startDate,
                    newSearchFilters.endDate
                );
                if (metadataResponse) {
                    dispatch(setWorkflowReportMetadataSearchResults(metadataResponse));
                    handleSearch(newSearchFilters, metadataResponse);
                }
            } else {
                dispatch(setWorkflowReportSearchFilters(newSearchFilters));
            }
        }
    };

    useEffect(() => {
        handleSearchParams();
    }, [searchParams, isLoaded]);

    const handleSearch = (newSearchFilters: WorkflowReportSearchFiltersState, metadataResponse?: WorkflowReportMetadataResponse) => {
        setIsStartedSearchResults(true);
        const { workflowId, appId, country, startDate, endDate } = newSearchFilters;

        let metadataSearchResultsToSave = metadataSearchResults;
        if (metadataResponse) {
            metadataSearchResultsToSave = {
                appIDs: metadataResponse.appIDs,
                countries: metadataResponse.countries,
                rpServices: metadataResponse.rpServices,
                isLoading: false,
                searchDate: new Date()
            };
        }

        dispatch(setWorkflowReportSearchFilters(newSearchFilters));
        dispatch(setWorkflowReportSearchedMetadataSearchResults(metadataSearchResultsToSave));
        getWorkflowReportSearchResultsReduxRequest(dispatch)(
            workflowId,
            appId,
            country,
            startDate,
            endDate
        );
    };

    const handleMetadataSearch = async (workflowId: string, startDate: Date, endDate: Date) => {
        if (
            metadataSearchFilters.workflowId !== workflowId ||
            metadataSearchFilters.startDate !== startDate ||
            metadataSearchFilters.endDate !== endDate
        ) {
            dispatch(setWorkflowReportMetadataSearchFilters({
                workflowId,
                startDate,
                endDate
            }));

            const metadataResponse = await getWorkflowReportMetadataSearchResultsReduxRequest(dispatch)(
                workflowId,
                startDate,
                endDate
            );
            return metadataResponse;
        }
        return null;
    };

    const isSearchResultsLoading = useMemo(() => {
        return (searchResults.isLoading.passRatesAndPassRateOverTime
            || searchResults.isLoading.completionRates
            || searchResults.isLoading.completionRatesOverTime
            || searchResults.isLoading.fraud
            || searchResults.isLoading.stepByStep
            || searchResults.isLoading.volumesByAppID
            || searchResults.isLoading.volumesByCountry)
    }, [
        searchResults.isLoading.passRatesAndPassRateOverTime,
        searchResults.isLoading.completionRates,
        searchResults.isLoading.completionRatesOverTime,
        searchResults.isLoading.fraud,
        searchResults.isLoading.stepByStep,
        searchResults.isLoading.volumesByAppID,
        searchResults.isLoading.volumesByCountry
    ]);

    return (
        <CardContainer>
            <ContentPageHeader
                title={resources.pages.workflowReport.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"workflow-report-page-header"}
                    />
                }
            />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <WorkflowReportSearchForm
                        requestInProgress={isSearchResultsLoading}
                        metadataRequestInProgress={metadataSearchResults.isLoading}
                        workflows={allowedSimpleAxnWorkflows}
                        countries={referenceData.axnCountries}
                        availableAppIds={metadataSearchResults.appIDs || []}
                        availableCountries={metadataSearchResults.countries || []}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                        handleMetadataSearch={handleMetadataSearch}
                    />
                )}
                {(isLoaded && isStartedSearchResults) && (
                    <WorkflowReportPassRatesContainer
                        passRatesResponse={searchResults.passRates}
                        passRatesOverTimeResponse={searchResults.passRatesOverTime}
                        rpServices={searchedMetadataSearchResults.rpServices}
                        searchFilters={searchFilters}
                        isLoading={searchResults.isLoading.passRatesAndPassRateOverTime}
                    />
                )}
                {(isLoaded && isStartedSearchResults) && (
                    <WorkflowReportCompletionRatesContainer
                        completionRatesResponse={searchResults.completionRates}
                        completionRatesOverTimeResponse={searchResults.completionRatesOverTime}
                        searchFilters={searchFilters}
                        isLoading={searchResults.isLoading.completionRates ||
                            searchResults.isLoading.completionRatesOverTime}
                    />
                )}
                {(isLoaded && isStartedSearchResults) && (
                    <WorkflowReportStepByStepContainer
                        stepByStepResponse={searchResults.stepByStep}
                        rpServices={searchedMetadataSearchResults.rpServices}
                        searchFilters={searchFilters}
                        isLoading={searchResults.isLoading.stepByStep}
                    />
                )}
                {(isLoaded && isStartedSearchResults) && (
                    <WorkflowReportVolumesByAppIdContainer
                        completionRatesResponse={searchResults.completionRates}
                        workflowReportVolumesByAppIdResponse={searchResults.volumesByAppID}
                        searchFilters={searchFilters}
                        isLoading={searchResults.isLoading.completionRates && searchResults.isLoading.volumesByAppID}
                    />
                )}
                {(isLoaded && isStartedSearchResults) && (
                    <WorkflowReportVolumesByCountryContainer
                        workflowReportVolumesByCountryResponse={searchResults.volumesByCountry}
                        axnCountries={referenceData.axnCountries}
                        searchFilters={searchFilters}
                        isLoading={searchResults.isLoading.volumesByCountry}
                    />
                )}
                {(isLoaded && isStartedSearchResults) && (
                    <WorkflowReportFraudContainer
                        completionRatesResponse={searchResults.completionRates}
                        fraudResponse={searchResults.fraud}
                        assertions={referenceData.axnAssertions}
                        riskLevels={referenceData.riskLevels}
                        searchFilters={searchFilters}
                        isLoading={searchResults.isLoading.fraud && searchResults.isLoading.completionRates}
                    />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};